import * as React from 'react'
import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'

import { cn } from '@/registry/reapit/lib/utils'

// DS sizes: Small = 32 x 32 px, Medium = 40 x 40 px.
// "large" / "lg" are backwards-compat aliases that render at 40 px (DS medium).
// data-size preserves the original prop; data-visual-size holds the normalised
// DS value ("small" | "medium") used by child component CSS selectors.
type AvatarSize = 'small' | 'medium' | 'large' | 'sm' | 'default' | 'lg'
type AvatarShape = 'circle' | 'square'
type AvatarColour = 'default' | 'primary'

function Avatar({
  className,
  size = 'medium',
  shape = 'circle',
  colour = 'default',
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: AvatarSize
  shape?: AvatarShape
  colour?: AvatarColour
}) {
  // Normalise legacy size aliases to the canonical three-value set expected by tests
  // and used for CSS data-attribute selectors.
  // Canonical: 'small' | 'medium' | 'large'
  const normalisedSize: 'small' | 'medium' | 'large' =
    size === 'sm' ? 'small' : size === 'default' ? 'medium' : size === 'lg' ? 'large' : size

  // DS visual size: small = 32 px, medium/large = 40 px (DS has no "large" - large -> medium)
  const visualSize: 'small' | 'medium' = normalisedSize === 'small' ? 'small' : 'medium'

  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={normalisedSize}
      data-visual-size={visualSize}
      data-shape={shape}
      data-colour={colour}
      className={cn(
        'group/avatar relative flex shrink-0 overflow-hidden select-none',
        // DS sizes: small = 32 px (size-8), medium = 40 px (size-10)
        visualSize === 'small' && 'size-8',
        visualSize === 'medium' && 'size-10',
        // Shape - circle for people, square (rounded-xl = 12 px) for entities
        shape === 'circle' && 'rounded-full',
        shape === 'square' && 'rounded-xl',
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image data-slot="avatar-image" className={cn('aspect-square size-full', className)} {...props} />
  )
}

function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center text-sm font-medium',
        // Default colour - DS: neutral-medium (#9FAEBC) background, foreground text (WCAG AA)
        'group-data-[colour=default]/avatar:bg-fill-neutral-medium group-data-[colour=default]/avatar:text-foreground',
        // Primary colour - DS: action-lightest (#ECF3FF) background, action text (#4E56EA)
        'group-data-[colour=primary]/avatar:bg-fill-action-lightest group-data-[colour=primary]/avatar:text-fill-action-dark',
        // Icon sizing based on the normalised visual size
        'group-data-[visual-size=small]/avatar:[&_svg]:size-4',
        'group-data-[visual-size=medium]/avatar:[&_svg]:size-5',
        // Shape inheritance handled by parent overflow-hidden
        className,
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background select-none',
        // Scale badge indicator with visual size
        'group-data-[visual-size=small]/avatar:size-2.5 group-data-[visual-size=small]/avatar:[&>svg]:hidden',
        'group-data-[visual-size=medium]/avatar:size-3 group-data-[visual-size=medium]/avatar:[&>svg]:size-2',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        'group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        // Default to medium size (40 px) - shrinks to small (32 px) when group contains small avatars
        'relative flex size-10 shrink-0 items-center justify-center rounded-full bg-fill-neutral-lightest text-sm text-muted-foreground ring-2 ring-background',
        'group-has-data-[visual-size=small]/avatar-group:size-8',
        '[&>svg]:size-4 group-has-data-[visual-size=small]/avatar-group:[&>svg]:size-3.5',
        className,
      )}
      {...props}
    />
  )
}

export { Avatar, AvatarImage, AvatarFallback, AvatarBadge, AvatarGroup, AvatarGroupCount }
